import { COMBO_CARDS, COMBO_COUNT, cloneRange } from './combos';
import { classifyCombo } from './classify';
import { applyRemoval } from './removal';
import { narrowMultiplier } from './action-model';
/**
 * Narrow `range` by the villain's observed response `evt`, given their `arch` and the `board`.
 * Multiplies each live combo's weight by the archetype's action probability for that combo's class,
 * then removes combos blocked by the board (and any extra `dead` cards, e.g. hero's hole cards).
 * Pure: returns a fresh WeightedRange.
 */
export const narrow = (range, evt, arch, board, dead = []) => {
    const out = cloneRange(range);
    const boardSet = new Set([...board, ...dead].map((c) => c));
    for (let idx = 0; idx < COMBO_COUNT; idx++) {
        const w = out.weights[idx];
        if (w === 0)
            continue;
        const [a, b] = COMBO_CARDS[idx];
        // combos using a dead card are handled by applyRemoval below; don't classify them.
        if (boardSet.has(a) || boardSet.has(b))
            continue;
        const { handClass } = classifyCombo([a, b], board);
        out.weights[idx] = w * narrowMultiplier(arch, evt, handClass);
    }
    return applyRemoval(out, [...board, ...dead]);
};
/** How many board cards are out on each street - used to classify combos against the board AS OF that
 *  street (a combo's hand class on the flop differs from the river). */
const STREET_BOARD_LEN = { preflop: 0, flop: 3, turn: 4, river: 5 };
/**
 * Walk the villain's postflop line and narrow their range street by street: for each step, classify the
 * range against the board AS OF that street and multiply by the archetype's action probabilities (via
 * `narrow`). Preflop steps and any step whose street has no board yet are skipped (preflop is already
 * encoded in `startingRange`). Pure: returns a fresh final range plus per-street snapshots. This is what
 * closes the coarse-range simplification - the villain's actual line drives the continuing range, so a
 * value-heavy fire-three-streets line tightens toward value, exactly as a reading player would infer.
 */
export const narrowThroughStreets = (input) => {
    const dead = input.dead ?? [];
    const byStreet = {};
    let range = input.startingRange;
    for (const step of input.steps) {
        const len = Math.min(STREET_BOARD_LEN[step.street], input.board.length);
        if (len < 3)
            continue; // no postflop board to classify against yet - skip (preflop is pre-encoded).
        const boardAtStreet = input.board.slice(0, len);
        range = narrow(range, step, input.arch, boardAtStreet, dead);
        byStreet[step.street] = range;
    }
    return { range, byStreet };
};
//# sourceMappingURL=narrow.js.map